// src/components/PhotoGallery.js

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LeftArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9.707 4.293a1 1 0 00-1.414 1.414L11.586 8H4a1 1 0 100 2h7.586l-3.293 3.293a1 1 0 001.414 1.414l5-5a1 1 0 000-1.414l-5-5z" clipRule="evenodd" />
  </svg>
);

const RightArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L13.586 11H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default function PhotoGallery({ photos = [], onComplete, onBack }) {
  const [validPhotos, setValidPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Filter out invalid photos
  useEffect(() => {
    const filtered = photos.filter(photo => photo && photo.trim() !== '');
    setValidPhotos(filtered);
  }, [photos]);

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleCloseFullscreen = () => {
    setSelectedPhoto(null);
  };

  if (validPhotos.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Galeria de Fotos
          </h3>
          <p className="text-gray-600">
            Nenhuma foto disponível na galeria.
          </p>
        </div>

        <div className="flex justify-between">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onBack}
            className="rounded-2xl py-3 px-6 flex items-center border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <LeftArrow /> Voltar
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onComplete([])}
            className="bg-[#e91e63] text-white rounded-2xl py-3 px-6 flex items-center hover:bg-[#d81557] transition-colors"
          >
            Próximo <RightArrow />
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Galeria de Fotos</h2>
        <p className="text-gray-600">Clique em uma foto para visualizá-la em tela cheia</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {validPhotos.map((photo, index) => (
          <motion.div
            key={photo}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow"
            onClick={() => handlePhotoClick(photo)}
          >
            <img
              src={photo}
              alt={`Foto ${index + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Error loading image:', photo);
                e.target.onerror = null;
                setValidPhotos(prev => prev.filter(p => p !== photo));
              }}
            />
          </motion.div>
        ))}
      </div>

      <div className="flex justify-between pt-6">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors flex items-center"
        >
          <LeftArrow /> Voltar
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onComplete([])}
          className="px-8 py-3 bg-[#e91e63] text-white rounded-xl hover:bg-[#d81557] transition-colors flex items-center"
        >
          Continuar <RightArrow />
        </motion.button>
      </div>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
            onClick={handleCloseFullscreen}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors p-2"
              onClick={handleCloseFullscreen}
            >
              <CloseIcon />
            </button>
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={selectedPhoto}
              alt="Foto em tela cheia"
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
